// src/components/admin/ModerationQueueTab.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Avatar,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ArticleIcon from '@mui/icons-material/Article';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';
import { getModerationQueue, moderatePost, ModeratePostPayload } from '../../services/adminService';
import { ModerationQueueResponse, PendingPost } from '../../types/admin';

const ModerationQueueTab: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<ModerationQueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [selectedPost, setSelectedPost] = useState<PendingPost | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [moderationNote, setModerationNote] = useState('');
  const [moderationAction, setModerationAction] = useState<'approved' | 'rejected' | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getModerationQueue();
      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch moderation queue:', err);
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (post: PendingPost, action: 'approved' | 'rejected') => {
    setSelectedPost(post);
    setModerationAction(action);
    setModerationNote('');
    setDialogOpen(true);
  };

  const handleModerate = async () => {
    if (!selectedPost || !moderationAction) return;

    try {
      setActionLoading(true);
      const payload: ModeratePostPayload = {
        moderation_status: moderationAction,
        moderation_note: moderationNote || undefined,
      };
      await moderatePost(selectedPost.id, payload);
      setDialogOpen(false);
      fetchData();
    } catch (err: any) {
      console.error('Failed to moderate post:', err);
      setError(err.response?.data?.detail || t('moderation.errorModerating'));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Il y a quelques minutes';
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getPostTypeStyle = (type: string | undefined | null) => {
    if (!type) return { color: '#64748b', bgColor: '#f1f5f9' };
    switch (type.toLowerCase()) {
      case 'profile':
        return { color: '#9333EA', bgColor: '#f3e8ff' };
      case 'report':
        return { color: '#ef4444', bgColor: '#fee2e2' };
      case 'verification':
        return { color: '#f59e0b', bgColor: '#fef3c7' };
      default:
        return { color: '#64748b', bgColor: '#f1f5f9' };
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight={700} color="#1e293b">
          {t('moderation.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gérez les contenus en attente de validation
        </Typography>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('moderation.totalPending')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color={data.summary.total_items > 0 ? '#d97706' : '#16a34a'} sx={{ mt: 0.5 }}>
                    {data.summary.total_items}
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: data.summary.total_items > 0 ? '#fef3c7' : '#dcfce7', borderRadius: 1 }}>
                  <PendingActionsIcon sx={{ color: data.summary.total_items > 0 ? '#d97706' : '#16a34a', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('moderation.pendingPosts')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {data.summary.total_pending_posts}
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 1 }}>
                  <ArticleIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('moderation.pendingFeedbacks')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {data.summary.pending_feedbacks}
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#f3e8ff', borderRadius: 1 }}>
                  <FeedbackIcon sx={{ color: '#7c3aed', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pending Posts */}
      <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <Box sx={{ p: 1, bgcolor: '#dbeafe', borderRadius: 1 }}>
              <ArticleIcon sx={{ color: '#2563eb', fontSize: 20 }} />
            </Box>
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              {t('moderation.pendingPostsTitle')}
            </Typography>
          </Stack>

          {data.pending_posts.length > 0 ? (
            <Stack spacing={2}>
              {data.pending_posts.map((post) => {
                const typeStyle = getPostTypeStyle(post.post_type);
                return (
                  <Card
                    key={post.id}
                    sx={{
                      border: '1px solid #e2e8f0',
                      boxShadow: 'none',
                      '&:hover': { borderColor: '#cbd5e1' },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                        <Box sx={{ flexGrow: 1 }}>
                          {/* Header */}
                          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                            <Box sx={{ px: 1.5, py: 0.5, bgcolor: typeStyle.bgColor, borderRadius: 0.5 }}>
                              <Typography variant="caption" fontWeight={600} sx={{ color: typeStyle.color, textTransform: 'uppercase' }}>
                                {post.post_type || 'contenu'}
                              </Typography>
                            </Box>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <AccessTimeIcon sx={{ fontSize: 14, color: '#64748b' }} />
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(post.created_at)}
                              </Typography>
                            </Stack>
                          </Stack>

                          {/* Title */}
                          <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                            {post.title}
                          </Typography>

                          {/* Content Preview */}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              mb: 2,
                            }}
                          >
                            {post.content}
                          </Typography>

                          {/* Author */}
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#2563eb', fontSize: 12 }}>
                              {post.author_name?.charAt(0).toUpperCase() || 'A'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500} color="#1e293b">
                                {post.author_name}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>

                        {/* Actions */}
                        <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} justifyContent="center">
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleOpenDialog(post, 'approved')}
                            sx={{ borderRadius: 1, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
                          >
                            {t('moderation.approve')}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<CancelIcon />}
                            onClick={() => handleOpenDialog(post, 'rejected')}
                            sx={{ borderRadius: 1 }}
                          >
                            {t('moderation.reject')}
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: '#dcfce7',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 40, color: '#16a34a' }} />
              </Box>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('moderation.noPendingPosts')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('moderation.allClear')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Moderation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => !actionLoading && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {selectedPost && (
          <>
            <DialogTitle sx={{ p: 0 }}>
              <Box sx={{ p: 3, bgcolor: moderationAction === 'approved' ? '#f0fdf4' : '#fef2f2', borderBottom: '1px solid #e2e8f0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.5,
                        bgcolor: moderationAction === 'approved' ? '#dcfce7' : '#fee2e2',
                        borderRadius: 0.5,
                        mb: 1.5,
                      }}
                    >
                      {moderationAction === 'approved' ? (
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                      ) : (
                        <CancelIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                      )}
                      <Typography variant="caption" fontWeight={600} sx={{ color: moderationAction === 'approved' ? '#16a34a' : '#dc2626' }}>
                        {moderationAction === 'approved' ? t('moderation.approveTitle') : t('moderation.rejectTitle')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={600} color="#1e293b">
                      {selectedPost.title}
                    </Typography>
                  </Box>
                  <IconButton onClick={() => setDialogOpen(false)} size="small" disabled={actionLoading} sx={{ bgcolor: 'white' }}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              {/* Post Preview */}
              <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0', mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {selectedPost.content}
                </Typography>
              </Box>

              {/* Note Field */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('moderation.note')}
                placeholder={t('moderation.notePlaceholder')}
                value={moderationNote}
                onChange={(e) => setModerationNote(e.target.value)}
                size="small"
              />
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setDialogOpen(false)} disabled={actionLoading} sx={{ borderRadius: 1 }}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                color={moderationAction === 'approved' ? 'success' : 'error'}
                onClick={handleModerate}
                disabled={actionLoading}
                sx={{
                  borderRadius: 1,
                  px: 3,
                  bgcolor: moderationAction === 'approved' ? '#16a34a' : '#dc2626',
                  '&:hover': { bgcolor: moderationAction === 'approved' ? '#15803d' : '#b91c1c' },
                }}
              >
                {actionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : moderationAction === 'approved' ? (
                  t('moderation.approve')
                ) : (
                  t('moderation.reject')
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ModerationQueueTab;
